import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import {
  GridApiPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';

import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '@/data/constants';

import triplegUtils from '@/services/triplegUtils';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '@/hooks/permissions/useIsSuperReaderUser';
import { useMinuteTick } from '@/hooks/useMinuteTick';

import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import {
  SyncedTripLeg,
  SyncedTripLegWithCareLevel,
} from '@/data/models/reservation.model';

import * as lookupSlice from '@/data/lookup.slice';

import triplegService from '@/services/triplegService';

import {
  ButtonException,
  CellActions,
  CellDualValue,
  CellSingleValue,
  DataGridPro,
} from '@/components/tables/DataGridPro';
import { ExceptionResolveDialog } from '@/components/home/triplegs/exceptions-resolve/ExceptionResolveDialog';

import {
  deriveMonitoringExceptionRow,
  formatTimeOnList,
  getExceptionName,
  MonitoringExceptionDerivedRow,
} from './monitoringExceptionsUtils';

interface MonitoringExceptionsProps {
  page: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  rowCount: number;
  handlePaginationChange: (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => void;
  handleSortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => void;
  tripLegs: TripLegSearchResponseItem[];
  loading: boolean;
}

// Subscribes to the minute tick itself so the elapsed time stays current
// without re-rendering the grid.
const TimeOnListCell = ({ startMillis }: { startMillis: number | null }) => {
  const now = useMinuteTick();
  return (
    <CellSingleValue>{formatTimeOnList(startMillis, now)}</CellSingleValue>
  );
};

const MonitoringExceptionsTable = ({
  page,
  pageSize,
  pageSizeOptions,
  rowCount,
  handlePaginationChange,
  handleSortModelChange,
  tripLegs,
  loading,
}: MonitoringExceptionsProps) => {
  const t = useCommonTranslation();
  const navigate = useNavigate();
  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const isSuperReaderUser = useIsSuperReaderUser();

  const [loadingTripLegData, setLoadingTripLegData] = useState(false);
  const [dialogResolveOpen, setDialogResolveOpen] = useState(false);
  const [selectedTripLeg, setSelectedTripLeg] =
    useState<SyncedTripLegWithCareLevel>();

  const rows = useMemo(
    () => tripLegs.map(deriveMonitoringExceptionRow),
    [tripLegs],
  );

  /* Start Button Click Handlers */
  const handleOpenResolveException = (
    evt: React.MouseEvent<HTMLElement>,
    trip: TripLegSearchResponseItem,
  ) => {
    evt.stopPropagation();
    getTripLeg(trip, () => {
      setDialogResolveOpen(true);
    });
  };

  const handleCloseResolveDialog = () => {
    setDialogResolveOpen(false);
  };

  const handleRowClick = (
    params: GridRowParams,
    _event: MuiEvent,
    _details: GridCallbackDetails,
  ) => {
    navigate(
      `/passengers/${params.row.passengerUUID}/reservations/${params.row.reservationReferenceID}?tripLegID=${params.row.tripLegReferenceID}`,
    );
  };
  /* End Button Click Handlers */

  const getTripLeg = (
    {
      clientBC,
      reservationReferenceID,
      passengerUUID,
      passengerFirstName,
      passengerLastName,
      tripLegReferenceID,
      tripLegExceptionBC,
      passengerCareLevelBC,
      tripModeBC,
    }: TripLegSearchResponseItem,
    callback: () => void,
  ) => {
    setLoadingTripLegData(true);
    triplegService
      .getTripLeg({ tripLegReferenceID })
      .subscribe((tripLeg: SyncedTripLeg) => {
        setSelectedTripLeg({
          ...tripLeg,
          clientBC,
          reservationReferenceID,
          passengerUUID,
          passengerFirstName,
          passengerLastName,
          tripLegExceptionBC,
          passengerCareLevelBC,
          tripModeBC,
        });
        if (callback) {
          callback();
        }
        setLoadingTripLegData(false);
      });
  };

  /* Start Column Render Functions */
  const renderPickupTime = useCallback(
    (params: GridCellParams) => {
      const tripLeg = params.row;
      const tripId = tripLeg.tripLegReferenceID;
      let timeString = '';

      if (tripLeg.isWillCall) {
        // This is the old code that conformed to the original spec. Apparently they want it to just show "Will Call" ticket F1V2-588
        // if (tripLeg.appointmentDate) {
        //   // Since the date has no time, we parse it and add 11 hours to ensure it displays the expected date.
        //   // If we didn't do this it would display the previous day since it parsed as a UTC date.
        //   const m = DateTime.fromISO(tripLeg.appointmentDate)
        //     .setZone(dateUtils.WORK_TIME_ZONE)
        //     .plus({ hours: 11 });
        //   timeString = `${m.toLocaleString(DateTime.TIME_SIMPLE)}, ${t('reservation.willCallTime')}`;
        // }
        timeString = t('reservation.willCallTime');
      } else {
        if (tripLeg.pickUpDueTimeUTC) {
          const tz = timezones.find(
            (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
          );
          const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
            zone: tz?.ianaStandard,
          });
          timeString = m.toLocaleString(DateTime.TIME_SIMPLE);
        }
      }
      return (
        <CellDualValue
          primaryValue={timeString || '-'}
          secondaryValue={tripId || ''}
        />
      );
    },
    [t, timezones],
  );

  const renderPickUpAddress = (params: GridRenderCellParams) => {
    const tripLeg = params.row;
    return (
      <CellDualValue
        primaryValue={`${tripLeg.pickUpAddressLine1}`}
        secondaryValue={`${tripLeg.pickUpCity} ${tripLeg.pickUpStateCode} ${tripLeg.pickUpZipCode.substring(0, 5)}`}
      />
    );
  };

  const renderDropOffAddress = (params: GridRenderCellParams) => {
    const tripLeg = params.row;
    return (
      <CellDualValue
        primaryValue={`${tripLeg.dropOffAddressLine1}`}
        secondaryValue={`${tripLeg.dropOffCity} ${tripLeg.dropOffStateCode} ${tripLeg.dropOffZipCode.substring(0, 5)}`}
      />
    );
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const passengerFirstName = params.row.passengerFirstName;
    const passengerLastName = params.row.passengerLastName;

    return (
      <CellDualValue
        primaryValue={`${passengerFirstName} ${passengerLastName}`}
        secondaryValue={`${params.row.tripModeBC}${params.row.passengerCareLevelBC}`}
      />
    );
  };

  const renderProviderName = (params: GridCellParams): React.ReactElement => {
    let providerName = params.row.providerName;
    let driverName = params.row.driverName;

    return (
      <CellDualValue
        primaryValue={providerName}
        secondaryValue={
          driverName ? driverName : t('monitoring.noDriverAssigned')
        }
      />
    );
  };

  const renderAssignee = (params: GridCellParams): React.ReactElement => {
    const assignee =
      triplegUtils.getExceptionAssignmentUserName(params.row) ||
      t('triplegInventory.unassigned');
    if (!assignee) {
      return (
        <CellSingleValue>{t('triplegInventory.unassigned')}</CellSingleValue>
      );
    }

    return (
      <CellDualValue
        primaryValue={assignee}
        secondaryValue={params.row.assigneeRole || ''}
      />
    );
  };

  const renderTimeOnList = (
    params: GridRenderCellParams<MonitoringExceptionDerivedRow>,
  ): React.ReactElement => (
    <TimeOnListCell startMillis={params.row.timeOnListStartMillis} />
  );

  const renderException = (
    params: GridRenderCellParams<MonitoringExceptionDerivedRow>,
  ): React.ReactElement => {
    const { etaMinutes, showEta, tripLegExceptionBC } = params.row;
    const etaDisplay =
      etaMinutes.length > 0
        ? etaMinutes
            .map((minutes) => `${minutes} ${t('common.minutes')}`)
            .join(', ')
        : '?';

    return (
      <CellDualValue
        primaryValue={getExceptionName(tripLegExceptionBC, t)}
        secondaryValue={
          showEta ? `${t('triplegInventory.eta')}: ${etaDisplay}` : ''
        }
      />
    );
  };

  const renderActions = (params: GridRenderCellParams) => {
    const isEscalated = params.row.isEscalated;
    return (
      <CellActions>
        {!isSuperReaderUser && !isEscalated && (
          <ButtonException
            onClick={(evt) => handleOpenResolveException(evt, params.row)}
            label={t('todaysTrips.resolveException')}
            data-fs-element="ResolveTripStartButton"
          />
        )}
      </CellActions>
    );
  };
  /* End Column Render Functions */

  const columns: GridColDef[] = [
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('triplegInventory.pickup'),
      flex: 0.75,
      sortable: true,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickUpAddress',
      headerName: t('todaysTrips.pickupAddress'),
      flex: 1.5,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderPickUpAddress,
    },
    {
      field: 'dropOffAddress',
      headerName: t('todaysTrips.dropoffAddress'),
      flex: 1.5,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderDropOffAddress,
    },
    {
      field: 'passengerFirstName',
      headerName: t('triplegInventory.member'),
      flex: 1,
      renderCell: renderMember,
    },
    {
      field: 'transportationProviderName',
      headerName: t('triplegInventory.providerTitle'),
      flex: 1,
      renderCell: renderProviderName,
    },
    {
      field: 'assignee',
      headerName: t('triplegInventory.assignee'),
      flex: 1,
      sortable: false,
      renderCell: renderAssignee,
    },
    {
      field: 'timeOnList',
      headerName: t('triplegInventory.timeOnList'),
      flex: 1,
      sortable: false,
      valueGetter: (params: GridCellParams<MonitoringExceptionDerivedRow>) =>
        params.row.timeOnListStartMillis,
      renderCell: renderTimeOnList,
    },
    {
      field: 'tripLegExceptionBC',
      headerName: t('triplegInventory.exception'),
      flex: 1,
      renderCell: renderException,
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: renderActions,
    },
  ];

  const isMemberContactException = triplegUtils.isMemberContactExceptionBC(
    selectedTripLeg?.tripLegExceptionBC,
  );

  return (
    <>
      <DataGridPro
        getRowId={(row) => `${row.tripLegReferenceID}`}
        rows={rows}
        rowCount={rowCount}
        columns={columns}
        pagination={true}
        paginationModel={{
          page: page - 1,
          pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
        }}
        onRowClick={handleRowClick}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS}
        onPaginationModelChange={handlePaginationChange}
        onSortModelChange={handleSortModelChange}
        autoHeight={true}
        loading={loading}
        disableRowSelectionOnClick={true}
        disableMultipleRowSelection={true}
      />
      {selectedTripLeg && dialogResolveOpen && (
        <ExceptionResolveDialog
          tripLeg={selectedTripLeg}
          open={dialogResolveOpen}
          close={handleCloseResolveDialog}
          userRoleBC="MER"
          isMemberContactException={isMemberContactException}
        />
      )}
    </>
  );
};

export { MonitoringExceptionsTable };
